import { Decimal } from '@snap-types/types/decimal';
import { getQuotePriceOrThrow } from '@ui/features/quote-builder/state/utilities/action-guards';
import { isEditableQuotePriceField } from '@ui/features/quote-builder/state/utilities/is-editable-quote-price-field';
import { z } from 'zod/v4';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';
import { parseNumberInput, validateDecimalPrecision } from './number-input';

type ValidateContributionMarginParams = {
  contributionMargin: string | Decimal;
  quoteLineUuid: string;
  quotePriceUuid: string;
};

const baseValidator = z
  .number({
    error: 'Contribution margin must be a number',
  })
  .lt(100, 'Contribution margin must be less than 100%');

export function validateContributionMargin(
  state: WritableQuoteBuilderStateType,
  {
    contributionMargin,
    quoteLineUuid,
    quotePriceUuid,
  }: ValidateContributionMarginParams,
) {
  const quotePrice = getQuotePriceOrThrow(state, quoteLineUuid, quotePriceUuid);
  const isEditable = isEditableQuotePriceField(
    quotePrice.priceStrategy,
    'contributionMargin',
  );

  let validator = baseValidator;
  if (isEditable) {
    validator = baseValidator.gt(
      -100,
      'Contribution margin must be greater than -100%',
    );
  }

  const contributionMarginString = contributionMargin.toString();

  const invalidIssueId = buildIssueId({
    area: 'quotePrice',
    quoteLineUuid,
    quotePriceUuid,
    type: 'invalid',
    field: 'contributionMargin',
  });

  const parsed = parseNumberInput({
    value: contributionMarginString,
    requiredMessage: 'Contribution margin is required',
    invalidMessage: 'Contribution margin must be a number',
  });

  if (parsed.error) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid contribution margin',
      description: parsed.error,
      severity: 'error',
      type: 'invalid',
      field: 'contributionMargin',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'contributionMargin',
        }),
      },
    });
  }

  const precisionError = validateDecimalPrecision({
    value: contributionMarginString,
    maxDecimalPlaces: 2,
    decimalPlacesMessage:
      'Contribution margin cannot have more than 2 decimal places',
  });

  if (precisionError) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid contribution margin',
      description: precisionError,
      severity: 'error',
      type: 'invalid',
      field: 'contributionMargin',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'contributionMargin',
        }),
      },
    });
  }

  const result = validator.safeParse(parsed.parsed);

  if (!result.success) {
    const error = z.flattenError(result.error).formErrors[0];

    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid contribution margin',
      description: error,
      severity: 'error',
      type: 'invalid',
      field: 'contributionMargin',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'contributionMargin',
        }),
      },
    });
  }

  deleteIssue(state, invalidIssueId);

  return new Decimal(result.data);
}
